import { Button, Form, Input, message, Modal, Select } from "antd";
import axios from "axios";
import { useEffect, useState, useRef } from "react";

import { API, Headers } from "../../Config";

const { Option } = Select;
const { TextArea } = Input;

function NotificationFormModal({ visible, onCancel, onSuccess }) {
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Use ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Fetch users for dropdown
  const fetchUsers = async () => {
    if (!isMountedRef.current) return;

    setLoading(true);
    try {
      const response = await axios.get(
        API.BASR_URL + API.USERS_LIST + `/list`,
        {
          headers: Headers(),
        }
      );

      // Only update state if component is still mounted
      if (isMountedRef.current && response.status === 200) {
        setUsers(response.data);
      }
    } catch (error) {
      // Only show error if component is still mounted
      if (isMountedRef.current) {
        message.error("Failed to fetch users");
      }
    } finally {
      // Only update loading state if component is still mounted
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    if (!isMountedRef.current) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        API.BASR_URL + "notifications/bulk",
        {
          userIds: values.userIds.map((id) => Number(id)),
          title: values.title,
          description: values.description,
          path: "/auth/home",
        },
        {
          headers: Headers(),
        }
      );

      // Only proceed if component is still mounted
      if (isMountedRef.current) {
        if (response.data) {
          message.success(`notifications sent successfully!`);
          form.resetFields();
          onSuccess();
          onCancel();
        } else {
          message.error(
            response.data.message || "Failed to send notifications"
          );
        }
      }
    } catch (error) {
      // Only show error if component is still mounted
      if (isMountedRef.current) {
        message.error(
          error.response?.data?.message || "Failed to send notifications"
        );
      }
    } finally {
      // Only update loading state if component is still mounted
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  // Handle modal cancel
  const handleCancel = () => {
    form.resetFields();
    setUsers([]);
    setLoading(false);
    setSubmitting(false);
    onCancel();
  };

  // Fetch users when modal opens
  useEffect(() => {
    if (visible) {
      isMountedRef.current = true;
      fetchUsers();
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, [visible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <Modal
      title="Send Notification"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
      maskClosable={false} // Prevent accidental closes
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          path: "/dashboard",
        }}
      >
        <Form.Item
          label="Select Users"
          name="userIds"
          rules={[
            { required: true, message: "Please select at least one user" },
            {
              type: "array",
              min: 1,
              message: "Please select at least one user",
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select users to send notification"
            loading={loading}
            showSearch
            filterOption={(input, option) => {
              const userName = option.children?.toLowerCase() || "";
              return userName.includes(input.toLowerCase());
            }}
            maxTagCount={3}
            maxTagTextLength={20}
            notFoundContent={loading ? "Loading..." : "No users found"}
          >
            {users.map((user) => (
              <Option key={user.id} value={Number(user.id)}>
                {user.name || `User ID: ${user.id}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Title"
          name="title"
          rules={[
            { required: true, message: "Please enter notification title" },
            { min: 3, message: "Title must be at least 3 characters" },
            { max: 255, message: "Title must not exceed 255 characters" },
          ]}
        >
          <Input placeholder="Enter notification title" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              message: "Please enter notification description",
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Enter notification description"
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item className="mb-0">
          <div className="d-flex justify-content-end gap-2">
            <Button onClick={handleCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={loading}
            >
              Send Notification
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default NotificationFormModal;
